import { debounce, isNull, noop } from 'lodash';
import {
  type CSSProperties,
  forwardRef,
  type MouseEvent,
  memo,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { useIsomorphicLayoutEffect } from 'react-use';

import { buildClientImgixUrl, isImgixUrl } from '@air/component-imgix-image';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { AnnotationsV2, type AnnotationsV2Props } from '~/components/AnnotationsV2/AnnotationsV2';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { useOptimizedImgixSrc } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/hooks/useOptimizedImgixSrc';
import { useSuitableImageUrl } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/hooks/useSuitableImageUrl';
import {
  ASSET_MODAL_MORPH_HERO_IMAGE_ATTRIBUTE,
  MAX_ANNOTATION_ZOOM,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/constants';
import { type ZoomToPointParams } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/types';
import { RefocusMode, useEditMode } from '~/components/EditMode/EditModeProvider';
import { useInlineSvgPreview } from '~/hooks/useInlineSvgPreview';
import useIsCmdPressed from '~/hooks/useIsCmdPressed';
import {
  centralizedClipDimensionsSelector,
  centralizedClipExtSelector,
  centralizedClipIdSelector,
  centralizedClipSelector,
} from '~/store/centralizedClip/selectors';
import { getClipImageAlt } from '~/utils/getClipImageAlt';
import {
  canRetryTransparentPreview,
  TRANSPARENT_PREVIEW_IMGIX_PARAMS,
  transparentPreviewDisplaySrc,
} from '~/utils/getTransparentPreviewSource';

const DEFAULT_IMG_SIZE = 2000;
const OVERLAY_BAR_HEIGHT = 64;
const VERTICAL_OVERLAY_COUNT = 2;
const VERTICAL_OVERLAY_HEIGHT = OVERLAY_BAR_HEIGHT * VERTICAL_OVERLAY_COUNT;
const ZOOM_DEBOUNCE_MS = 200;

type ImageVisualizerChildrenParams = {
  imageElementWidth: number;
  imageElementHeight: number;
  enableImageMouseEvents: (enable: boolean) => void;
  zoomToPoint: (params: ZoomToPointParams) => void;
  currentZoom: number;
  isZooming: boolean;
};

export type ForwardedImageVisualizerClipProps = {
  handleMouseStart?: (event: MouseEvent<HTMLDivElement>) => void;
  handleMouseMove?: (event: MouseEvent<HTMLDivElement>) => void;
  handleMouseStop?: (event: MouseEvent<HTMLDivElement>) => void;
  isFullScreen: boolean;
  isShowOriginal: boolean;
  isShowSolidBackground: boolean;
  zoom: number;
  imageRef: RefObject<HTMLImageElement>;
  canAnnotateClip: boolean;
  children?: (params: ImageVisualizerChildrenParams) => ReactNode;
  resetZoom: (options?: { instant?: boolean }) => void;
  zoomToPoint: (params: ZoomToPointParams) => void;
  overrideImageContainerStyle?: CSSProperties;
  overrideImageSrc?: string;
  onImageError?: () => void;
  onImageLoad?: () => void;
  onImageReset?: () => void;
} & Pick<AnnotationsV2Props, 'AnnotationsAvatars' | 'AnnotationComment' | 'FloatingCommentForm'>;

const ForwardedImageVisualizerClip = forwardRef<HTMLDivElement, ForwardedImageVisualizerClipProps>(
  (
    {
      handleMouseStart = noop,
      handleMouseMove = noop,
      handleMouseStop = noop,
      isFullScreen,
      isShowOriginal,
      isShowSolidBackground,
      zoom,
      imageRef,
      canAnnotateClip,
      AnnotationsAvatars,
      AnnotationComment,
      FloatingCommentForm,
      children,
      zoomToPoint,
      resetZoom,
      overrideImageContainerStyle,
      overrideImageSrc,
      onImageError,
      onImageLoad,
      onImageReset,
    }: ForwardedImageVisualizerClipProps,
    containerRef,
  ) => {
    const { isAboveMediumScreen } = useBreakpointsContext();
    const clip = useSelector(centralizedClipSelector);
    const imageAlt = getClipImageAlt(clip, 'displayTitle');
    const clipId = useSelector(centralizedClipIdSelector);
    const clipExt = useSelector(centralizedClipExtSelector);
    const [error, setError] = useState(false);
    const [failedSrc, setFailedSrc] = useState<string | undefined>();
    const [isPanning, setIsPanning] = useState(false);
    const [isZooming, setIsZooming] = useState(false);
    const [mouseEventsEnabled, setMouseEventsEnabled] = useState(true);
    const measuredContainerRef = useRef<HTMLDivElement>(null);
    const previousClipIdRef = useRef(clipId);
    const { fallbackUrl, preserveTransparency, processedPreviewSrc, src } = useSuitableImageUrl(isShowOriginal);
    const displaySrc = transparentPreviewDisplaySrc({ failedSrc, processedPreviewSrc, src });
    const showCheckerboard = preserveTransparency && displaySrc === src;
    const { inlineSvgPreviewUrl, markInlineSvgPreviewFailed, isInlineSvgPreviewPending } = useInlineSvgPreview({
      src,
      ext: clipExt,
      enabled: showCheckerboard && !error && !overrideImageSrc,
    });
    // Hold the raw SVG back until normalization settles, so it never paints and then swaps.
    const isAwaitingInlineSvgPreview = showCheckerboard && isInlineSvgPreviewPending;

    /**
     * Reset zoom synchronously (before the browser paints the new clip) so a previously
     * zoomed/panned version never renders for a frame with a stale transform on switch.
     */
    useIsomorphicLayoutEffect(() => {
      if (previousClipIdRef.current === clipId) {
        return;
      }

      previousClipIdRef.current = clipId;
      resetZoom({ instant: true });
      setError(false);
      setIsPanning(false);
      onImageReset?.();
    }, [clipId, onImageReset, resetZoom]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset zoom only when component unmounts, adding `resetZoom` as dependency would cause an infinite loop due to `resetZoom` relying on `zoom`.
    useEffect(() => {
      return () => {
        resetZoom();
      };
    }, []);

    const { height: originalImageHeight, width: originalImageWidth } = useSelector(centralizedClipDimensionsSelector);

    const isCmdPressed = useIsCmdPressed();

    const disableZooming = useMemo(
      () =>
        debounce(() => {
          setIsZooming(false);
        }, ZOOM_DEBOUNCE_MS),
      [],
    );

    useEffect(() => {
      if (zoom !== 1) {
        setIsZooming(true);
        disableZooming();
      }
    }, [disableZooming, zoom]);

    const isAnnotationsEnabled = useAnnotationV2Store((state) => state.isAnnotationsEnabled);

    const canStartAnnotation = canAnnotateClip && isAboveMediumScreen && !isFullScreen;
    const canDrawAnnotation = canStartAnnotation && isAnnotationsEnabled && !isCmdPressed;

    const isZoomedIn = zoom > 1 && (!canDrawAnnotation || isCmdPressed);

    const { height: measuredHeight = 0, width: measuredWidth = 0 } = useResizeDetector<HTMLDivElement>({
      handleHeight: true,
      targetRef: measuredContainerRef,
    });

    const containerWidth = measuredWidth || 0;
    const containerHeight = measuredHeight || 0;

    useIsomorphicLayoutEffect(() => {
      setError(false);
      setIsPanning(false);
      onImageReset?.();
    }, [onImageReset, overrideImageSrc, src]);

    const { optimizedImgixSrc: processedOverrideImageSrc } = useOptimizedImgixSrc({
      src: overrideImageSrc,
      containerWidth,
    });

    const imgxUrl = useMemo(() => {
      if (!displaySrc) {
        return '';
      }

      if (processedOverrideImageSrc) {
        return processedOverrideImageSrc;
      }

      // Normalized SVG data URL: Imgix would return the raw SVG without fonts or intrinsic size.
      if (inlineSvgPreviewUrl && showCheckerboard) {
        return inlineSvgPreviewUrl;
      }

      if (isAwaitingInlineSvgPreview) {
        return '';
      }

      if (!isImgixUrl(displaySrc)) {
        return displaySrc;
      }

      return buildClientImgixUrl(
        displaySrc,
        isShowOriginal
          ? /**
             * If we want to show the original without it being resized, we need to make sure that no parameters are
             * passed over to Imigix otherwise it will apply the maximum width/height of 8192px on the image.
             */
            undefined
          : {
              w:
                !isNull(originalImageWidth) && originalImageWidth < DEFAULT_IMG_SIZE
                  ? originalImageWidth
                  : DEFAULT_IMG_SIZE,
              h:
                !isNull(originalImageHeight) && originalImageHeight > DEFAULT_IMG_SIZE
                  ? originalImageHeight
                  : DEFAULT_IMG_SIZE,
              cs: 'origin',
              ...(showCheckerboard ? TRANSPARENT_PREVIEW_IMGIX_PARAMS : {}),
            },
        { src: displaySrc, disableSrcSet: true },
      );
    }, [
      displaySrc,
      inlineSvgPreviewUrl,
      isAwaitingInlineSvgPreview,
      isShowOriginal,
      originalImageHeight,
      originalImageWidth,
      processedOverrideImageSrc,
      showCheckerboard,
    ]);

    const imageFitStyle = useMemo<CSSProperties>(() => {
      const availableHeight = isFullScreen ? containerHeight : Math.max(containerHeight - VERTICAL_OVERLAY_HEIGHT, 0);
      const shouldStretchSmallImage =
        isFullScreen && measuredHeight > originalImageHeight && measuredWidth > originalImageWidth;

      return {
        // Constrain the base image box at 1x before paint; zoom uses CSS transform and can overflow under overlays.
        maxHeight: availableHeight > 0 ? `${availableHeight}px` : `calc(100% - ${VERTICAL_OVERLAY_HEIGHT}px)`,
        maxWidth: originalImageWidth > containerWidth || isFullScreen ? '100%' : `${originalImageWidth}px`,
        height: shouldStretchSmallImage ? '100%' : 'auto',
        width: shouldStretchSmallImage ? '100%' : 'auto',
      };
    }, [
      containerHeight,
      containerWidth,
      isFullScreen,
      measuredHeight,
      measuredWidth,
      originalImageHeight,
      originalImageWidth,
    ]);

    const handleError = useCallback(
      (event: SyntheticEvent<HTMLImageElement>) => {
        // A failed data URL retries the Imgix original once; only Imgix failures drop transparency.
        const erroredSrc = event.currentTarget.currentSrc || event.currentTarget.src;

        if (
          !processedOverrideImageSrc &&
          inlineSvgPreviewUrl &&
          showCheckerboard &&
          erroredSrc === inlineSvgPreviewUrl
        ) {
          markInlineSvgPreviewFailed(event);
          setIsPanning(false);

          return;
        }

        if (!processedOverrideImageSrc && canRetryTransparentPreview({ failedSrc, processedPreviewSrc, src })) {
          setFailedSrc(src);
          setError(false);
          setIsPanning(false);

          return;
        }

        setError(true);
        setIsPanning(false);
        onImageError?.();
      },
      [
        failedSrc,
        inlineSvgPreviewUrl,
        onImageError,
        markInlineSvgPreviewFailed,
        processedOverrideImageSrc,
        processedPreviewSrc,
        showCheckerboard,
        src,
      ],
    );

    // Use V1's approach: measure actual image dimensions with useResizeDetector
    const { height: imageHeight, width: imageWidth } = useResizeDetector<HTMLDivElement>({
      handleHeight: true,
      targetRef: imageRef,
    });

    const { imageElementHeight, imageElementWidth } = useMemo(() => {
      if (!originalImageHeight || !originalImageWidth) {
        return { imageElementHeight: 0, imageElementWidth: 0 };
      }

      const imageRatio = originalImageWidth / originalImageHeight;

      let imageElementHeight: number;
      let imageElementWidth: number;

      if (originalImageHeight > originalImageWidth) {
        imageElementHeight = imageHeight ?? 0;
        imageElementWidth = imageElementHeight * imageRatio;
      } else {
        imageElementWidth = imageWidth ?? 0;
        imageElementHeight = imageElementWidth / imageRatio;
      }

      return {
        imageElementHeight,
        imageElementWidth,
      };
    }, [imageHeight, imageWidth, originalImageHeight, originalImageWidth]);

    const {
      isEditModeEnabled,
      imageRef: editModeImageRef,
      imageOffset,
      boundingBox: { scale, refocusMode },
    } = useEditMode();

    /**
     * Outside edit mode the zoom transform is owned imperatively by `useImageZoom` (on the same
     * <img>), so React must NOT also write `transform` here or it would clobber the live zoom on
     * every re-render. In edit mode zoom is forced to 1, so React safely owns the offset/scale.
     */
    const imageStyle = useMemo<CSSProperties>(() => {
      if (!isEditModeEnabled) {
        return imageFitStyle;
      }

      return {
        ...imageFitStyle,
        transform: `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${scale})`,
        transformOrigin: refocusMode === RefocusMode.BOX ? 'top left' : 'center center',
      };
    }, [imageFitStyle, imageOffset.x, imageOffset.y, isEditModeEnabled, refocusMode, scale]);

    const Image = useMemo(() => {
      const imgElement =
        error && fallbackUrl ? (
          // biome-ignore lint/performance/noImgElement: required for low-level control
          <img
            className={cn(
              'flex size-auto max-w-full origin-center object-contain',
              showCheckerboard && !isShowSolidBackground && 'transparency-checkerboard',
            )}
            src={fallbackUrl}
            alt={imageAlt}
            style={imageStyle}
          />
        ) : (
          // biome-ignore lint/performance/noImgElement: required for low-level control
          <img
            data-testid="ASSET_MODAL_CLIP_IMAGE"
            {...{ [ASSET_MODAL_MORPH_HERO_IMAGE_ATTRIBUTE]: '' }}
            className={cn(
              'flex size-auto max-w-full origin-center object-contain',
              showCheckerboard && !isShowSolidBackground && 'transparency-checkerboard',
              // Hide the src-less box and its alt text while the SVG preview is pending.
              isAwaitingInlineSvgPreview && 'invisible',
            )}
            ref={mergeRefs([imageRef, editModeImageRef])}
            draggable={false}
            alt={imageAlt}
            // An empty src fires `error`; omitting it keeps the pending SVG out of the error/fallback path.
            src={isAwaitingInlineSvgPreview ? undefined : imgxUrl}
            onError={handleError}
            onLoad={onImageLoad}
            style={imageStyle}
          />
        );

      return <div style={overrideImageContainerStyle}>{imgElement}</div>;
    }, [
      imageAlt,
      error,
      fallbackUrl,
      imageRef,
      imageStyle,
      imgxUrl,
      isAwaitingInlineSvgPreview,
      isShowSolidBackground,
      handleError,
      onImageLoad,
      overrideImageContainerStyle,
      showCheckerboard,
      editModeImageRef,
    ]);

    const panDisableCountRef = useRef(0);

    // false acquires a pan-disable lock; true releases one. Multiple overlays can hold locks independently.
    const enableMouseEvents = useCallback((enable: boolean) => {
      if (enable) {
        panDisableCountRef.current = Math.max(0, panDisableCountRef.current - 1);
      } else {
        panDisableCountRef.current += 1;
      }
      setMouseEventsEnabled(panDisableCountRef.current === 0);
    }, []);

    // Disable showing context menu when user clicks left mouse button with meta key pressed
    // we allow to move image around when meta key is pressed and annotation is being drawn
    const disableMetaLeftContextMenu = useCallback((e: MouseEvent<HTMLDivElement>) => {
      // Only prevent default for left mouse button with META key (Cmd on macOS)
      // Allow Ctrl+click to show context menu, but prevent Cmd+click to allow dragging
      if (e.button === 0 && e.metaKey) {
        e.preventDefault();
      }
      // Don't prevent for Ctrl+click or right-clicks - allow context menus
    }, []);

    const canUseMouseEvents = isCmdPressed || (!canDrawAnnotation && mouseEventsEnabled);

    const shouldRenderAnnotations = !isZooming && zoom < MAX_ANNOTATION_ZOOM;

    const handlePanMouseStart = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (isZoomedIn) {
          setIsPanning(true);
        }

        handleMouseStart(event);
      },
      [handleMouseStart, isZoomedIn],
    );

    const handlePanMouseStop = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        setIsPanning(false);
        handleMouseStop(event);
      },
      [handleMouseStop],
    );

    let imageCursorClassName = 'cursor-auto';

    if (isZoomedIn) {
      imageCursorClassName = isPanning ? 'cursor-grabbing' : 'cursor-grab';
    }

    return (
      <div // NOSONAR -- pointer pan stays on the image; a button role would replace the picture name
        role="img"
        aria-label={imageAlt}
        className={cn(
          'relative flex size-full max-h-full touch-none select-none items-center justify-center',
          imageCursorClassName,
        )}
        onContextMenu={disableMetaLeftContextMenu}
        onMouseDown={canUseMouseEvents ? handlePanMouseStart : undefined}
        onMouseMove={canUseMouseEvents ? handleMouseMove : undefined}
        onMouseUp={canUseMouseEvents ? handlePanMouseStop : undefined}
        onMouseLeave={canUseMouseEvents ? handlePanMouseStop : undefined}
        ref={mergeRefs([containerRef, measuredContainerRef])}
        data-image-zoom-surface=""
        data-testid="IMAGE_THEATER"
      >
        {Image}
        {shouldRenderAnnotations ? (
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: `${imageElementWidth * zoom}px`,
              height: `${imageElementHeight * zoom}px`,
              pointerEvents: 'auto',
              overflow: 'hidden', // Prevent annotations outside bounds
            }}
          >
            <AnnotationsV2
              AnnotationsAvatars={AnnotationsAvatars}
              AnnotationComment={AnnotationComment}
              FloatingCommentForm={FloatingCommentForm}
              pageNumber={-1}
              canDrawAnnotation={canDrawAnnotation}
              keyShortcutsEnabled={canStartAnnotation}
              width={imageElementWidth * zoom}
              height={imageElementHeight * zoom}
            />
          </div>
        ) : null}

        {children?.({
          imageElementWidth: imageElementWidth * zoom || 0,
          imageElementHeight: imageElementHeight * zoom || 0,
          enableImageMouseEvents: enableMouseEvents,
          zoomToPoint,
          currentZoom: zoom,
          isZooming,
        })}
      </div>
    );
  },
);

ForwardedImageVisualizerClip.displayName = 'ImageVisualizerClip';

export const ImageVisualizerClip = memo(ForwardedImageVisualizerClip);

ImageVisualizerClip.displayName = 'MemoizedImageVisualizerClip';
